/**
 * Role-Based Sidebar Menu Configuration
 *
 * Roles:
 * - Admin / HR Admin: Full access
 * - Director: Strategic level access
 * - Manager: Department/team management
 * - HOD (Head of Department): Department-specific access
 * - Employee: Self-service access
 */

// Define role constants for consistency
export const ROLES = {
  ADMIN: 'Admin',
  HR_ADMIN: 'HR Admin',
  HR_ASSISTANT: 'HR Assistant',
  DIRECTOR: 'Director',
  MANAGER: 'Manager',
  HOD: 'HOD',
  EMPLOYEE: 'Employee',
  SUPER_ADMIN: 'Super Admin',
};

// Helper function to check if user has access to a menu item
export const hasMenuAccess = (menuItem, userRole) => {
  // If no roles specified, menu is accessible to all
  if (!menuItem.roles || menuItem.roles.length === 0) {
    return true;
  }

  // Check if user's role is in the allowed roles
  return menuItem.roles.includes(userRole);
};

// Main sidebar data with role-based permissions
export const sidebarMenuData = [
  {
    tittle: 'MAIN',
    showAsTab: false,
    separateRoute: false,
    roles: [], // Accessible to all roles
    menu: [
      {
        menuValue: 'Dashboard',
        hasSubRoute: false,
        showSubRoute: false,
        route: "/dashboard",
        icon: "la la-dashcube",
        roles: [], // All users can access dashboard
      },
    ],
  },
  {
    tittle: 'HR',
    showAsTab: false,
    separateRoute: false,
    roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.HR_ASSISTANT, ROLES.DIRECTOR, ROLES.MANAGER, ROLES.HOD],
    menu: [
      {
        menuValue: 'Employees',
        hasSubRoute: true,
        showSubRoute: false,
        route: "#",
        icon: "la la-user",
        roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.HR_ASSISTANT, ROLES.DIRECTOR, ROLES.MANAGER, ROLES.HOD],
        subMenus: [
          {
            menuValue: 'All Employees',
            route: "/employees",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.HR_ASSISTANT, ROLES.DIRECTOR, ROLES.MANAGER, ROLES.HOD],
          },
          {
            menuValue: 'Leaves',
            route: "/adminleaves",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.HR_ASSISTANT, ROLES.MANAGER, ROLES.HOD],
          },
          {
            menuValue: 'Loans',
            route: "/loans",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.HR_ASSISTANT],
          },
          {
            menuValue: 'Transfers',
            route: "/transfers",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          },
        ],
      },
      {
        menuValue: 'Accounting',
        hasSubRoute: true,
        showSubRoute: false,
        icon: "la la-files-o",
        roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
        subMenus: [
          {
            menuValue: 'Categories',
            route: "/categories",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          },
          {
            menuValue: 'Budgets',
            route: "/budgets",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          },
          {
            menuValue: 'Budget Revenues',
            route: "/budget-revenues",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          },
          {
            menuValue: 'Budget Expenses',
            route: "/budget-expenses",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          },
        ],
      },
      {
        menuValue: 'Payroll',
        hasSubRoute: true,
        showSubRoute: false,
        icon: "la la-money",
        roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.HR_ASSISTANT],
        subMenus: [
          {
            menuValue: 'Employee Salary',
            route: "/salary",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.HR_ASSISTANT],
          },
          {
            menuValue: 'Payslip',
            route: "/salary-view",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.HR_ASSISTANT],
          },
          {
            menuValue: 'Payroll Items',
            route: "/payroll-items",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN],
          },
        ],
      },
      {
        menuValue: 'Policies',
        hasSubRoute: false,
        showSubRoute: false,
        route: "/policies",
        icon: "la la-file-pdf-o",
        roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR, ROLES.HR_ASSISTANT],
      },
      {
        menuValue: "Reports",
        hasSubRoute: true,
        showSubRoute: false,
        icon: "la la-chart-pie",
        roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.HR_ASSISTANT, ROLES.DIRECTOR, ROLES.MANAGER, ROLES.HOD],
        subMenus: [
          {
            menuValue: "Payslip Report",
            route: "/payslip-reports",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.HR_ASSISTANT],
          },
          {
            menuValue: "Daily Attendance",
            route: "/daily-attendance-report",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.HR_ASSISTANT, ROLES.MANAGER, ROLES.HOD],
          },
          {
            menuValue: "First In Last Out",
            route: "/first-in-last-out-report",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.HR_ASSISTANT, ROLES.MANAGER, ROLES.HOD],
          },
          {
            menuValue: "Time Card",
            route: "/time-card-report",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.HR_ASSISTANT, ROLES.MANAGER, ROLES.HOD],
          },
          {
            menuValue: "Attendance Summary",
            route: "/attendance-summary-report",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.HR_ASSISTANT, ROLES.MANAGER, ROLES.HOD],
          },
          {
            menuValue: "Leave Report",
            route: "/leave-reports",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.HR_ASSISTANT, ROLES.MANAGER, ROLES.HOD],
          },
        ]
      }
    ],
  },
  {
    tittle: 'PERFORMANCE',
    showAsTab: false,
    separateRoute: false,
    roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR, ROLES.MANAGER, ROLES.HOD],
    menu: [
      {
        menuValue: "Performance",
        hasSubRoute: true,
        showSubRoute: false,
        icon: "la la-graduation-cap",
        roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR, ROLES.MANAGER, ROLES.HOD],
        subMenus: [
          {
            menuValue: "Performance Indicator",
            route: "/performance-indicator",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          },
          {
            menuValue: "Performance Review",
            route: "/performance",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR, ROLES.MANAGER, ROLES.HOD],
          },
          {
            menuValue: "Performance Appraisal",
            route: "/performance-appraisal",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR, ROLES.MANAGER, ROLES.HOD],
          }
        ]
      },
      {
        menuValue: "Goals",
        hasSubRoute: true,
        showSubRoute: false,
        icon: "la la-crosshairs",
        roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR, ROLES.MANAGER, ROLES.HOD],
        subMenus: [
          {
            menuValue: "Goal List",
            route: "/goal-tracking",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR, ROLES.MANAGER, ROLES.HOD],
          },
          {
            menuValue: "Goal Type",
            route: "/goal-type",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          }
        ]
      },
      {
        menuValue: "Training",
        hasSubRoute: true,
        showSubRoute: false,
        icon: "la la-edit",
        roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR, ROLES.MANAGER, ROLES.HOD],
        subMenus: [
          {
            menuValue: "Training List",
            route: "/training",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR, ROLES.MANAGER, ROLES.HOD],
          },
          {
            menuValue: "Trainers",
            route: "/trainers",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          },
          {
            menuValue: "Training Type",
            route: "/training-type",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          }
        ]
      },
      {
        menuValue: 'Promotion',
        hasSubRoute: false,
        showSubRoute: false,
        route: "/promotion",
        icon: "la la-bullhorn",
        roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
      },
      {
        menuValue: 'Resignation',
        hasSubRoute: false,
        showSubRoute: false,
        route: "/resignation",
        icon: "la la-external-link-square",
        roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR, ROLES.MANAGER, ROLES.HOD],
      },
      {
        menuValue: 'Termination',
        hasSubRoute: false,
        showSubRoute: false,
        route: "/termination",
        icon: "la la-times-circle",
        roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
      },
    ],
  },
  {
    tittle: 'ADMINISTRATION',
    showAsTab: false,
    separateRoute: false,
    roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
    menu: [
      {
        menuValue: "Jobs",
        hasSubRoute: true,
        showSubRoute: false,
        icon: "la la-briefcase",
        roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
        subMenus: [
          {
            menuValue: "Jobs Dasboard",
            route: "/jobs-dashboard",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          },
          {
            menuValue: "Manage Jobs",
            route: "/jobs",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          },
          {
            menuValue: "Manage Resumes",
            route: "/manage-resumes",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          },
          {
            menuValue: "Shortlist Candidates",
            route: "/shortlist-candidates",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          },
          {
            menuValue: "Interview Questions",
            route: "/interview-questions",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          },
          {
            menuValue: "Offer Approvals",
            route: "/offer_approvals",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          },
          {
            menuValue: "Experience Level",
            route: "/experiance-level",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN],
          },
          {
            menuValue: "Candidates List",
            route: "/candidates",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          },
          {
            menuValue: "Schedule timing",
            route: "/schedule-timing",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN],
          },
          {
            menuValue: "Aptitude Results",
            route: "/apptitude-result",
            roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.DIRECTOR],
          }
        ]
      },
      {
        menuValue: 'Communication',
        hasSubRoute: false,
        showSubRoute: false,
        route: "/communication",
        icon: "la la-bullhorn",
        roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.SUPER_ADMIN],
      },
      {
        menuValue: 'Settings',
        hasSubRoute: false,
        showSubRoute: false,
        route: "/company-settings",
        icon: "la la-cog",
        roles: [ROLES.ADMIN, ROLES.HR_ADMIN, ROLES.SUPER_ADMIN],
      },
    ],
  },
  // Employee Self-Service Section
  {
    tittle: 'MY SPACE',
    showAsTab: false,
    separateRoute: false,
    roles: [ROLES.EMPLOYEE, ROLES.HOD, ROLES.MANAGER, ROLES.HR_ADMIN, ROLES.HR_ASSISTANT], // For employees and management roles
    menu: [
      {
        menuValue: 'My Profile',
        hasSubRoute: false,
        showSubRoute: false,
        route: "/profile",
        icon: "la la-user",
        roles: [], // All users
      },
      {
        menuValue: 'My Leaves',
        hasSubRoute: false,
        showSubRoute: false,
        route: "/leaves-employee",
        icon: "la la-calendar",
        roles: [], // All users
      },
      {
        menuValue: 'My Attendance',
        hasSubRoute: false,
        showSubRoute: false,
        route: "/attendance-employee",
        icon: "la la-clock-o",
        roles: [], // All users
      },
      {
        menuValue: 'My Payslips',
        hasSubRoute: false,
        showSubRoute: false,
        route: "/my-payslips",
        icon: "la la-money",
        roles: [], // All users
      },
      {
        menuValue: 'My Loans',
        hasSubRoute: false,
        showSubRoute: false,
        route: "/my-loans",
        icon: "la la-bank",
        roles: [], // All users can request loans
      },
      {
        menuValue: 'Guarantor Requests',
        hasSubRoute: false,
        showSubRoute: false,
        route: "/guarantor-requests",
        icon: "la la-handshake-o",
        roles: [], // All users can view guarantor requests
      },
      {
        menuValue: 'Policies',
        hasSubRoute: false,
        showSubRoute: false,
        route: "/policies",
        icon: "la la-file-pdf-o",
        roles: [], // All users can view policies
      },
    ],
  },
];

/**
 * Filter sidebar menu based on user role
 * @param {string} userRole - The role of the current user
 * @returns {Array} Filtered menu data
 */
export const getFilteredSidebarData = (userRole) => {
  if (!userRole) return [];

  const filterMenuItems = (items) => {
    return items
      .filter(item => hasMenuAccess(item, userRole))
      .map(item => {
        const filteredItem = { ...item };

        // Filter submenus if they exist
        if (item.subMenus && item.subMenus.length > 0) {
          filteredItem.subMenus = item.subMenus.filter(subItem =>
            hasMenuAccess(subItem, userRole)
          );

          // If all submenus are filtered out, don't show the parent menu
          if (filteredItem.subMenus.length === 0) {
            return null;
          }
        }

        return filteredItem;
      })
      .filter(item => item !== null);
  };

  return sidebarMenuData
    .filter(section => hasMenuAccess(section, userRole))
    .map(section => ({
      ...section,
      menu: filterMenuItems(section.menu),
    }))
    .filter(section => section.menu.length > 0);
};

// Backward compatibility exports
export const adminSidebarData = sidebarMenuData;
export const employeeSidebarData = getFilteredSidebarData(ROLES.EMPLOYEE);
